import { useRef, useState, useEffect } from "react";

const formatValue = (value) => {
  if (new RegExp(/^\d{2}:\d{2}$/).test(value)) return value;
  if (typeof value === "string") {
    const timeMatch = value.match(/(\d{1,2}):(\d{1,2})/);
    if (timeMatch === true) {
      const hours = timeMatch[1].padStart(2, "0");
      const minutes = timeMatch[2].padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  }
  return "";
};

const TimeInput = ({ value, onChange, className = "", isInError = false, ...rest }) => {
  const inputRef = useRef(null);
  const shortcutOffsetRef = useRef(0);
  const [inputValue, setInputValue] = useState(formatValue(value));

  useEffect(() => {
    if (value === inputValue || !value) return;
    setInputValue(formatValue(value));
  }, [value]);

  const onShortcutKeyDown = (event) => {
    if (event.ctrlKey || event.metaKey) return;
    if (event.key !== " " && event.key !== "-" && event.key !== "+") return;
    event.preventDefault();
    if (event.key === " ") shortcutOffsetRef.current = 0;
    if (event.key === "-") shortcutOffsetRef.current -= 1;
    if (event.key === "+") shortcutOffsetRef.current += 1;
    const date = new Date();
    date.setHours(date.getHours() + shortcutOffsetRef.current);
    const formattedValue = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    setInputValue(formattedValue);
    onChange(formattedValue);
  };

  const handleChange = (event) => {
    shortcutOffsetRef.current = 0;
    const inputElement = event.target;
    const rawValue = inputElement.value;
    if (typeof rawValue !== "string") return;

    const selectionStart = typeof inputElement.selectionStart === "number" ? inputElement.selectionStart : rawValue.length;
    const rawCharacters = rawValue.split("");
    const sanitizedCharacters = [];
    const sanitizedIndices = new Array(rawCharacters.length + 1);

    let digitCount = 0;
    let colonIndex = undefined;
    for (let index = 0; index < rawCharacters.length; index += 1) {
      sanitizedIndices[index] = sanitizedCharacters.length;
      const character = rawCharacters[index];
      const isDigitCharacter = character >= "0" && character <= "9";
      if (isDigitCharacter === true) {
        if (digitCount === 4) continue;
        sanitizedCharacters.push(character);
        digitCount += 1;
        continue;
      }
      if (character === ":" && index === 2) {
        if (colonIndex !== undefined) continue;
        sanitizedCharacters.push(character);
        colonIndex = index;
      }
    }
    sanitizedIndices[rawCharacters.length] = sanitizedCharacters.length;

    const sanitizedValue = sanitizedCharacters.join("");
    const digitsOnlyValue = sanitizedValue.replace(/\D/g, "");
    const hoursSegment = digitsOnlyValue.slice(0, 2);
    const minutesSegment = digitsOnlyValue.slice(2, 4);
    const shouldIncludeColon = digitsOnlyValue.length > 2 || colonIndex !== undefined;

    let formattedValue = "";
    if (hoursSegment.length > 0) formattedValue += hoursSegment;
    if (shouldIncludeColon === true) formattedValue += ":";
    if (minutesSegment.length > 0) formattedValue += minutesSegment;

    const sanitizedCaretPosition = sanitizedIndices[selectionStart];
    const sanitizedBeforeCaret = sanitizedValue.slice(0, sanitizedCaretPosition);
    const digitsBeforeCaret = sanitizedBeforeCaret.replace(/\D/g, "").length;
    const hasColonBeforeCaret = sanitizedBeforeCaret.indexOf(":") !== -1;

    let nextCaretPosition = digitsBeforeCaret;
    if (shouldIncludeColon === true && (digitsBeforeCaret > 2 || hasColonBeforeCaret === true)) nextCaretPosition += 1;
    if (nextCaretPosition > formattedValue.length) nextCaretPosition = formattedValue.length;

    setInputValue(formattedValue);

    setTimeout(() => {
      if (inputRef.current === null) return;
      inputRef.current.setSelectionRange(nextCaretPosition, nextCaretPosition);
    }, 0);

    if (formattedValue.length !== 5) return onChange(null);

    const hours = parseInt(hoursSegment, 10);
    const minutes = parseInt(minutesSegment, 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      onChange(formattedValue);
      return;
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      maxLength={5}
      placeholder="HH:MM"
      className={`ghost-input ${className}`}
      data-state={isInError ? "error" : undefined}
      value={inputValue}
      onChange={handleChange}
      onKeyDown={onShortcutKeyDown}
      onFocus={() => {
        shortcutOffsetRef.current = 0;
      }}
      {...rest}
    />
  );
};

export default TimeInput;